import { TERR, DEPT_IDS, TRANSLATION_LANGS, DEFAULT_PRICE_TABLE, TABLE_COLS } from '../config/constants';
import { getFieldDefs } from '../config/fields';

let _idCounter = 1;
export const uid = () => `id_${Date.now()}_${_idCounter++}`;

// 'bra' is legacy naming from Lounge's origin — it really just means "two-dimension variant"
// (e.g. Band + Cup, or any tenant's own opt1/opt2 preset). Kept as a recognized value for
// existing stored product data; new writes use the generic name.
export const TWO_DIM_VARIANT_TYPE = 'twoDim';
export const isTwoDimVariant = (variantType) => variantType === 'bra' || variantType === TWO_DIM_VARIANT_TYPE;

export function convertPrice(gbpPrice, col, table = DEFAULT_PRICE_TABLE, cols = TABLE_COLS) {
  if (!col) return parseFloat(gbpPrice || 0).toFixed(2);
  const gbp = parseFloat(gbpPrice);
  if (isNaN(gbp)) return '';
  const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
  if (!keys.length) return gbp.toFixed(2);
  let closest = keys[0];
  for (const k of keys) {
    if (Math.abs(k - gbp) < Math.abs(closest - gbp)) closest = k;
  }
  return String(table[closest]?.[cols.indexOf(col)] ?? '');
}

export function currentMonthLabel() {
  return new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' });
}

// Studio media lives in one ordered `images` array; a video entry carries `kind: 'video'`.
// Older entries have no `kind`, so fall back to the file extension.
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm)(?:\?|$)/i;
export const VIDEO_MIME_RE = /^video\/(mp4|quicktime|webm|x-m4v)$/;
export const isVideoMedia = (m) => m?.kind === 'video' || VIDEO_EXT_RE.test(m?.filename || m?.url || '');
export const isVideoFile  = (f) => VIDEO_MIME_RE.test(f?.type || '') || VIDEO_EXT_RE.test(f?.name || '');
// Videos go to their own bucket so product-images keeps its tight image-only limits.
export const mediaBucketFor = (file) => isVideoFile(file) ? 'product-videos' : 'product-images';

// Keeps the flat `imageUrl` / `additionalImages` fields (what CSV export and Shopify push
// read) in step with the ordered `images` array (what the UI edits). Call it on every
// change to `images`. Videos are left out — a CSV has nowhere to put them, and the primary
// must be a still — so the primary is the first image, wherever it sits in the order.
export function syncImagesFields(images) {
  const stills = images.filter(m => !isVideoMedia(m));
  return {
    imageUrl:         stills[0]?.url || '',
    additionalImages: stills.slice(1).map(i => i.url).join(', '),
  };
}

// Every media URL for an images CSV, in display order but with the primary still moved to
// position 1 (Shopify features position 1, and that must not be a video). Videos are kept:
// Matrixify imports them from the same Image Src column as stills.
export function exportMediaUrls(p) {
  const urls = Array.isArray(p.images) && p.images.length > 0
    ? p.images.map(m => (typeof m === 'string' ? m : m?.url)).filter(Boolean)
    : [p.imageUrl, ...(p.additionalImages || '').split(',')].map(u => (u || '').trim()).filter(Boolean);
  const primary = p.imageUrl || '';
  return primary ? [primary, ...urls.filter(u => u !== primary)] : urls;
}

export function makeProduct(overrides = {}, tenantConfig = null) {
  // TERR/DEPT_IDS/TRANSLATION_LANGS only apply when tenantConfig is entirely absent (e.g. a
  // direct call with no tenant context, tests). The real per-request tenantConfig always comes
  // from useTenantConfig(), which deliberately leaves these as [] — never undefined — until a
  // tenant configures them in Settings, so this fallback never fires in the live app.
  const territories = tenantConfig?.territories ?? TERR;
  const deptIds     = tenantConfig?.deptIds     ?? DEPT_IDS;
  const transLangs  = tenantConfig?.translationLangs ?? TRANSLATION_LANGS;
  const terrKeys    = territories.map(t => t.code);
  return {
    id: uid(),
    skuBase: '', handle: '', title: '', titleNoColour: '',
    vendor: '',
    type: '', productCategory: '', tags: [],
    published: 'TRUE', shopifyStatus: 'draft',
    bodyHtml: '', composition: '', fabricWeight: '',
    fitFabric: '', careInstructions: '',
    basePrice: '', compareAtPrice: '', publishDate: '',
    imageUrl: '', additionalImages: '', images: [], cadImage: null,
    metaColor: '',
    hsCode: '', coo: '',
    translations: Object.fromEntries(transLangs.map(l => [l.code, { title: '', bodyHtml: '', composition: '', fitFabric: '', careInstructions: '' }])),
    ...Object.fromEntries((tenantConfig?.metafields ?? []).map(f => [f.key, ''])),
    metaBadges: [],
    variantType: 'size', variants: [],
    shopifyConnections: {},
    warehouseConnections: {},
    territoryPrices:  Object.fromEntries(terrKeys.map(k => [k, ''])),
    territoryEnabled: Object.fromEntries(terrKeys.map(k => [k, true])),
    sectionStatus:    Object.fromEntries(deptIds.map(d => [d, 'empty'])),
    sectionNotes:     Object.fromEntries(deptIds.map(d => [d, ''])),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...overrides,
  };
}

export function makeCollection(name, month = currentMonthLabel()) {
  return {
    id: uid(), name, month, products: [],
    springboard: {
      designer: '', developer: '', retailChannel: 'online', limitedEdition: false,
    },
  };
}

// Whether a scored field counts as "filled". Variant-scoped fields are checked against a specific
// variant (with a couple of product-level fallbacks that mirror the original scorer); everything
// else is product-level. titleNoColour carries no weight — it's auto-derived (see deriveTitleNoColour).
function fieldFilled(p, v, f) {
  if (f.scope === 'variant') {
    if (f.key === 'sku')     return !!v.sku;
    if (f.key === 'size')    return !!(v.size || v.opt1Val);
    if (f.key === 'barcode') return !!v.barcode;
    if (f.key === 'hsCode')  return !!(v.hsCode || p.hsCode);
    if (f.key === 'coo')     return !!(v.coo || p.coo);
    return !!(v[f.key] && String(v[f.key]).trim());
  }
  if (f.key === 'tags') return (p.tags?.length || 0) > 0;
  return !!(p[f.key] && String(p[f.key]).trim());
}

// Completion score for one department: the share of that department's scored fields that are
// filled. Which fields score is now read from the field definitions (config/fields), not a
// hardcoded list — a field a tenant disables, removes, or adds with scored set flows through here.
export function calcSectionScore(p, dept, tenantConfig = null) {
  const disabled = tenantConfig?.disabledFields || [];
  const scored   = getFieldDefs(tenantConfig).filter(f => f.scored === dept && !disabled.includes(f.key));
  const variantFields = scored.filter(f => f.scope === 'variant');
  const productFields = scored.filter(f => f.scope !== 'variant');

  // Variant-scoped scoring (Ops): filled fields summed across every variant.
  if (variantFields.length) {
    const variants = p.variants || [];
    if (variants.length === 0) return 0;
    const filled = variants.reduce((sum, v) => sum + variantFields.filter(f => fieldFilled(p, v, f)).length, 0);
    return Math.round((filled / (variants.length * variantFields.length)) * 100);
  }

  // Product-scoped scoring (everyone else): filled / total, evenly weighted.
  if (!productFields.length) return 100; // nothing left to score → trivially complete
  const filled = productFields.filter(f => fieldFilled(p, null, f)).length;
  return Math.round((filled / productFields.length) * 100);
}

export function calcOverallScore(p, tenantConfig = null) {
  const deptIds = tenantConfig?.deptIds ?? DEPT_IDS;
  return Math.round(deptIds.reduce((sum, d) => sum + calcSectionScore(p, d, tenantConfig), 0) / deptIds.length);
}

export function autoAdvanceStatus(p, tenantConfig = null) {
  const deptIds = tenantConfig?.deptIds ?? DEPT_IDS;
  const sectionStatus = { ...p.sectionStatus };
  deptIds.forEach(d => {
    const current = sectionStatus[d] || 'empty';
    const score = calcSectionScore(p, d, tenantConfig);
    if (score === 100 && (current === 'empty' || current === 'progress')) {
      sectionStatus[d] = 'review';
    } else if (score > 0 && score < 100 && current === 'empty') {
      sectionStatus[d] = 'progress';
    } else if (score < 100 && current === 'approved') {
      sectionStatus[d] = 'progress';
    }
  });
  return { ...p, sectionStatus };
}

// Derives "title minus a trailing colour" (e.g. "Ramen Social Tee - Lilac" → "Ramen Social
// Tee"). Titles with no " - <colour>" suffix are returned unchanged — it "handles colour or
// not" rather than requiring one. This is the single source of truth for the titleNoColour
// concept: it's never a field a person has to fill in, only an explicit CSV-imported override
// (p.titleNoColour) takes precedence over it, everywhere that value is needed.
export function deriveTitleNoColour(title) {
  if (!title) return '';
  const m = title.match(/^(.+?)\s*-\s*[^-]+$/);
  return (m ? m[1] : title).trim();
}

// ─────────────────────────────────────────────────────────────
// PRODUCT GROUPING — shared by Dashboard (colour groups) and Springboard (Group by style).
// Grouping key/field is tenant-configurable (config.groupingField); '' disables grouping
// entirely.
// ─────────────────────────────────────────────────────────────

function getGroupKey(p, tenantConfig = null) {
  const field = tenantConfig?.groupingField ?? 'titleNoColour';
  if (!field) return null;
  const val = p[field];
  if (val && String(val).trim()) return String(val).trim();
  if (field === 'titleNoColour') return deriveTitleNoColour(p.title) || null;
  return null;
}

// Returns [{ key, items }] — singletons/unmatched products get key=null (unique per product).
export function buildProductGroups(products, tenantConfig = null) {
  const map = new Map();
  const order = [];
  for (const p of products) {
    const key = getGroupKey(p, tenantConfig);
    if (key) {
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key).push(p);
    } else {
      const solo = '__solo__' + p.id;
      map.set(solo, [p]);
      order.push(solo);
    }
  }
  return order.map(k => ({ key: k.startsWith('__solo__') ? null : k, items: map.get(k) }));
}
